import { useCallback } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';

import { useKaiCoachService } from './useKaiCoachService';

type EarnedByUnit = Record<string, number>;

/**
 * A student's earned points per unit for each of the given coaches, keyed by
 * component id, so the course homepage can tell which coach deadlines they have
 * already met. One query per coach, run beside the page rather than inside it:
 * a coach whose query is pending or failed is simply absent, so its deadlines
 * stay listed. Staff never ask (enabled is false for them).
 */
export function useCoachDueProgress(
  courseId: string | undefined,
  componentIds: string[],
  enabled: boolean,
): Record<string, EarnedByUnit> {
  const { service, userId } = useKaiCoachService();
  const ready = enabled && Boolean(service) && Boolean(courseId) && Boolean(userId);

  // Stable while the ids are, so the combined map keeps its identity until a
  // query's data actually changes.
  const combine = useCallback(
    (results: Array<UseQueryResult<EarnedByUnit>>) =>
      Object.fromEntries(
        results.flatMap((result, index) =>
          result.data ? [[componentIds[index], result.data] as const] : [],
        ),
      ),
    [componentIds],
  );

  return useQueries({
    combine,
    queries: componentIds.map((componentId) => ({
      enabled: ready,
      queryFn: () =>
        service!.loadEarnedByUnit({ componentId, courseId: courseId!, userId }),
      queryKey: ['coachDueProgress', courseId, componentId, userId],
      retry: false,
    })),
  });
}
